import { Icons } from '@/app/components/ui/Icons'
import classNames from 'classnames'
import { FC, useMemo, useState } from 'react'
import { CircleFlag } from 'react-circle-flags'
import { CustomScroll } from 'react-custom-scroll'
import '@/app/styles/components/MobileOptionSelector.css'

export interface MobileSelectorItem {
	id: string
	flag: string
	title: string
	subtitle: string
	searchableText?: string // Additional text to search (e.g., English name for languages)
}

interface MobileOptionSelectorProps {
	isOpen: boolean
	items: MobileSelectorItem[]
	selectedItemId: string
	onSelect: (item: MobileSelectorItem) => void
	id?: string
	testId?: string
	enableSearch?: boolean
	searchPlaceholder?: string
	showIcon?: boolean
}

const MobileOptionSelector: FC<MobileOptionSelectorProps> = ({
	isOpen,
	items,
	selectedItemId,
	onSelect,
	id,
	testId,
	enableSearch = true,
	searchPlaceholder = 'Search...',
	showIcon = true,
}) => {
	const [searchQuery, setSearchQuery] = useState('')

	const filteredItems = useMemo(() => {
		if (!searchQuery.trim()) return items
		const query = searchQuery.toLowerCase()
		const filtered = items.filter(
			(item) =>
				item.title.toLowerCase().includes(query) ||
				item.subtitle.toLowerCase().includes(query) ||
				(item.searchableText && item.searchableText.toLowerCase().includes(query))
		)
		// Return all items if no matches found (consistent with other components)
		return filtered.length > 0 ? filtered : items
	}, [items, searchQuery])

	return (
		<div
			className="mobile-option-selector__wrapper"
			id={id || 'mobile-option-selector'}
			data-testid={testId}
		>
			<div
				className={classNames('mobile-option-selector__content', {
					'mobile-option-selector__content--open': isOpen,
				})}
			>
				{enableSearch && (
					<div className="mobile-selector-search">
						<Icons.iconLens className="mobile-selector-search__icon" />
						<input
							type="text"
							placeholder={searchPlaceholder}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="mobile-selector-search__input"
							data-testid={testId ? `${testId}-search-input` : undefined}
						/>
					</div>
				)}
				<div className="mobile-option-selector__scroll-container">
					<CustomScroll allowOuterScroll={false} heightRelativeToParent="100%">
						{filteredItems.map((item: MobileSelectorItem, index: number) => (
							<div
								onClick={() => onSelect(item)}
								key={`${item.id}-${index}`}
								className="mobile-selector-item"
								id={`mobile_selector_item_${item.id}`}
								data-testid={testId ? `${testId}-item-${item.id}` : undefined}
							>
								{showIcon && (
									<div className="mobile-selector-item__icon">
										<CircleFlag
											countryCode={item.flag.toLowerCase()}
											className="mobile-selector-item__flag"
										/>
									</div>
								)}
								<div className="mobile-selector-item__info">
									<span
										className="mobile-selector-item__title"
										data-lang-code={item.id}
									>
										{item.title}
									</span>
									<span className="mobile-selector-item__subtitle">
										{item.subtitle}
									</span>
								</div>
								<Icons.iconCheck
									stroke="var(--dark-blue)"
									className={classNames('mobile-selector-item__check', {
										'mobile-selector-item__check--visible': item.id === selectedItemId,
										'mobile-selector-item__check--hidden': item.id !== selectedItemId,
									})}
								/>
							</div>
						))}
					</CustomScroll>
				</div>
			</div>
		</div>
	)
}

export default MobileOptionSelector
